// A single student that bulkPublishResults could not publish, paired with the
// reason. Type comes from GraphQL codegen (the schema's BulkPublishFailure).
import type { BulkPublishFailure } from '@/types/composition-functions';

export type { BulkPublishFailure };

/** Shape of an individual failure as it arrives from the (untyped) GraphQL response. */
interface RawBulkPublishFailure {
  student_id?: string | number | null;
  student_name?: string | null;
  reason?: string | null;
}

interface BulkPublishResponseLike {
  failures?: RawBulkPublishFailure[] | null;
}

function isRenderableFailure(
  failure: RawBulkPublishFailure | null | undefined,
): failure is RawBulkPublishFailure & { reason: string } {
  return !!failure && typeof failure.reason === 'string' && failure.reason.length > 0;
}

/**
 * Normalise the bulkPublishResults response into a clean, typed list of failures.
 *
 * Guards against a missing/null `failures` field and drops malformed entries (no usable
 * reason) so the UI can render `student_name — reason` rows without further checks.
 */
export function extractBulkPublishFailures(
  response: BulkPublishResponseLike | null | undefined,
): BulkPublishFailure[] {
  const failures = response?.failures;
  if (!Array.isArray(failures)) return [];

  return failures.filter(isRenderableFailure).map((failure) => ({
    student_id: String(failure.student_id ?? ''),
    student_name: failure.student_name ?? '',
    reason: failure.reason,
  }));
}
